export const STATUTS_SYNC_ACTIFS = ['EN_ATTENTE_AGENT', 'RECUPERATION', 'ANALYSE'];
export const STATUTS_SYNC_TERMINAUX = ['TERMINEE', 'PARTIELLE', 'ERREUR', 'ANNULEE'];
const STATUTS_SYNC_ERREUR = ['PARTIELLE', 'ERREUR', 'ANNULEE'];
export const DUREE_SUIVI_INTERCLUB_MAX_MS = 30 * 60 * 1000;
export const DELAI_VEILLE_INTERCLUB_MS = 120 * 1000;

const ageExecutionInterclub = (execution, maintenant = Date.now()) => {
  const reference = new Date(execution?.date_recuperation || execution?.date_demande || 0).valueOf();
  if (!reference || !Number.isFinite(reference)) return null;
  return Math.max(0, Number(maintenant) - reference);
};

export function delaiPollingInterclub(execution, maintenant = Date.now()) {
  if (deriveInterclubJobState(execution) !== 'RUNNING') return null;
  const age = ageExecutionInterclub(execution, maintenant);
  if (age == null) return 60 * 1000;
  if (age < 2 * 60 * 1000) return 5 * 1000;
  if (age < 10 * 60 * 1000) return 15 * 1000;
  if (age < DUREE_SUIVI_INTERCLUB_MAX_MS) return 60 * 1000;
  return DELAI_VEILLE_INTERCLUB_MS;
}

export function doitVerifierSuiviInterclub(execution, maintenant = Date.now()) {
  if (deriveInterclubJobState(execution) !== 'RUNNING') return false;
  const age = ageExecutionInterclub(execution, maintenant);
  return age != null && age >= DUREE_SUIVI_INTERCLUB_MAX_MS;
}

const cleSuivi = (groupe, suffixe) => `crm-clubs:${String(groupe || '').trim()}:interclub-suivi:${suffixe}`;

export const CLES_SUIVI_INTERCLUB = {
  masque: (groupe) => cleSuivi(groupe, 'job-masque'),
  sonNotifie: (groupe) => cleSuivi(groupe, 'job-son-notifie'),
  sonActive: (groupe) => cleSuivi(groupe, 'son-active'),
};

const lireStockage = (storage, cle) => {
  try { return storage?.getItem?.(cle) || ''; } catch { return ''; }
};

const ecrireStockage = (storage, cle, valeur) => {
  try { storage?.setItem?.(cle, String(valeur || '')); return true; } catch { return false; }
};

export function lireJobInterclubMasque(storage, groupe) {
  return lireStockage(storage, CLES_SUIVI_INTERCLUB.masque(groupe));
}

export function masquerSuiviInterclub(storage, groupe, jobId) {
  return ecrireStockage(storage, CLES_SUIVI_INTERCLUB.masque(groupe), jobId);
}

export function reouvrirSuiviInterclub(storage, groupe) {
  try { storage?.removeItem?.(CLES_SUIVI_INTERCLUB.masque(groupe)); return true; } catch { return false; }
}

export function doitAfficherSuiviInterclub(suivi, jobMasque) {
  if (!suivi?.jobId) return false;
  return suivi.active || !suivi.terminale || String(jobMasque || '') !== String(suivi.jobId);
}

export function transitionSonoreInterclub(precedent, courant) {
  if (!precedent?.jobId || precedent.jobId !== courant?.jobId || precedent.etat !== 'RUNNING') return null;
  if (courant.etat === 'DONE') return 'SUCCES';
  if (courant.etat === 'ERROR') return 'ERREUR';
  return null;
}

export function nombreBipsNotificationInterclub(signal) {
  if (signal === 'SUCCES') return 2;
  if (signal === 'ERREUR') return 3;
  return 0;
}

export function sonInterclubPrefere(storage, groupe) {
  return lireStockage(storage, CLES_SUIVI_INTERCLUB.sonActive(groupe)) === '1';
}

export function memoriserSonInterclubActif(storage, groupe) {
  return ecrireStockage(storage, CLES_SUIVI_INTERCLUB.sonActive(groupe), '1');
}

export async function notifierSyncInterclubUneFois({ storage, locks, groupe, jobId, notifier }) {
  if (!jobId || typeof notifier !== 'function') return false;
  const cle = CLES_SUIVI_INTERCLUB.sonNotifie(groupe);
  const executer = async () => {
    if (lireStockage(storage, cle) === String(jobId)) return false;
    await notifier();
    ecrireStockage(storage, cle, jobId);
    return true;
  };
  if (locks?.request) return locks.request(`${cle}:${jobId}`, { mode: 'exclusive' }, executer);
  return executer();
}

const LIBELLES = {
  DEVIS: 'DEVIS', COMMANDE: 'COMMANDES', FACTURE: 'FACTURES', CLIENT: 'CLIENTS',
  AVOIR: 'AVOIRS', APPLICATION: 'APPLICATION',
};

export function dureeSync(demarrage, maintenant = Date.now()) {
  const debut = new Date(demarrage || 0).valueOf();
  if (!debut || !Number.isFinite(debut)) return '—';
  const secondes = Math.max(0, Math.floor((Number(maintenant) - debut) / 1000));
  return `${String(Math.floor(secondes / 60)).padStart(2, '0')}:${String(secondes % 60).padStart(2, '0')}`;
}

export function causeSyncLisible(execution) {
  const progression = execution?.compteurs?.progression || {};
  const etape = progression.etape || null;
  const causeProgression = progression.etapes?.[etape]?.cause;
  const terminaleEnErreur = STATUTS_SYNC_ERREUR.includes(execution?.statut);
  const brut = String((terminaleEnErreur ? execution?.erreur_technique : null) || causeProgression || execution?.erreur_technique || '').trim();
  const pagination = brut.match(/Pagination\s+([A-Z_]+)\s+bloqu[ée]e:\s*(\d+)\/(\d+)/i);
  if (pagination) return { etape: pagination[1].toUpperCase(), cause: `Pagination incomplète ${pagination[2]}/${pagination[3]}.` };
  if (/Catégorie\s+CLIENT\s+vide ou non conforme/i.test(brut)) return { etape: 'CLIENT', cause: 'Catégorie CLIENT non lue ou non conforme.' };
  return { etape, cause: brut || null };
}

export function deriveInterclubJobState(execution) {
  const statut = execution?.statut || null;
  if (STATUTS_SYNC_ERREUR.includes(statut)) return 'ERROR';
  if (statut === 'TERMINEE') return 'DONE';
  if (STATUTS_SYNC_ACTIFS.includes(statut)) return 'RUNNING';
  return execution?.id ? 'WAITING' : 'ABSENT';
}

export function suiviSyncInterclub(execution, maintenant = Date.now()) {
  if (!execution?.id) return null;
  const progression = execution.compteurs?.progression || {};
  const resultatsFinaux = execution.categories_resultats && typeof execution.categories_resultats === 'object' ? execution.categories_resultats : {};
  const sourceEtapes = Object.keys(progression.etapes || {}).length
    ? progression.etapes
    : Object.fromEntries(Object.entries(resultatsFinaux).map(([nom, detail]) => [nom, { statut: detail?.statut === 'SUCCES' ? 'TERMINEE' : detail?.statut, uniques: detail?.nombre_pieces }]));
  const etapes = Object.entries(sourceEtapes).map(([nom, detail]) => {
    const normalise = { ...(detail || {}) };
    if (!Number.isFinite(normalise.fenetres) && Number.isFinite(normalise.pages)) normalise.fenetres = normalise.pages;
    delete normalise.pages;
    delete normalise.pagesAttendues;
    return { nom, libelle: LIBELLES[nom] || nom, ...normalise };
  });
  const etapesTerminees = etapes.filter((etape) => etape.statut === 'TERMINEE').length;
  const etat = deriveInterclubJobState(execution);
  const actives = etat === 'RUNNING';
  const terminee = etat === 'DONE';
  const erreur = etat === 'ERROR';
  const aVerifier = doitVerifierSuiviInterclub(execution, maintenant);
  return {
    jobId: execution.id,
    statut: execution.statut,
    titre: aVerifier ? 'M.A.J INTERCLUB — SUIVI À VÉRIFIER' : actives ? 'M.A.J INTERCLUB EN COURS' : terminee ? 'M.A.J INTERCLUB TERMINÉE' : erreur ? 'M.A.J INTERCLUB INTERROMPUE' : 'M.A.J INTERCLUB',
    active: actives,
    aVerifier,
    terminale: STATUTS_SYNC_TERMINAUX.includes(execution.statut),
    demarrage: execution.date_recuperation || execution.date_demande,
    duree: dureeSync(execution.date_recuperation || execution.date_demande, actives ? maintenant : new Date(execution.date_application || execution.updated_at || maintenant).valueOf()),
    etape: progression.etape || null,
    etapes,
    etapesTerminees,
    nombreEtapes: etapes.length,
    erreur: erreur ? causeSyncLisible(execution) : null,
  };
}
